import { cn } from "@tcg/ui";
import { Minus, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { MoneyText, useYen } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";

import {
  clampCount,
  COINS,
  countTotal,
  NOTES,
  rowTotal,
  type Counts,
  type DenominationValue,
} from "./denominations";

export interface DenominationCounterProps {
  counts: Counts;
  onChange: (counts: Counts) => void;
  /** ¥2,000 notes are rare — hidden when the shop says so (settings). */
  hideTwoThousand?: boolean;
  disabled?: boolean;
  /** The action under the total (開ける / 締める). */
  children?: ReactNode;
  label: string;
}

/**
 * Count the drawer by tapping the number of pieces (design/05 §3.7): notes on the left, coins in
 * the middle, the number pad + the counted total + the action on the right — all on one tablet
 * screen (1024×768) without scrolling. Tap a row's number to type it on the pad, or use − / +.
 */
export function DenominationCounter({
  counts,
  onChange,
  hideTwoThousand = false,
  disabled = false,
  children,
  label,
}: DenominationCounterProps) {
  const { t } = useTranslation();
  const yen = useYen();
  const notes = NOTES.filter((v) => !(hideTwoThousand && v === 2000 && !counts[2000]));
  const [active, setActive] = useState<DenominationValue>(10000);
  const set = (value: DenominationValue, count: number) =>
    onChange({ ...counts, [value]: clampCount(count) });
  const total = countTotal(counts);

  const row = (value: DenominationValue) => {
    const count = counts[value] ?? 0;
    const name = yen(value);
    return (
      <div
        key={value}
        role="group"
        aria-label={name}
        className={cn(
          "grid min-h-pos-touch grid-cols-[5.5rem_auto_1fr] items-center gap-2 rounded-md border px-2",
          active === value && "border-primary bg-accent",
        )}
      >
        <span className="text-lg font-semibold tabular-nums">{name}</span>
        <span className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t("shift.count_minus", { value: name })}
            disabled={disabled || count === 0}
            onClick={() => {
              setActive(value);
              set(value, count - 1);
            }}
            className="flex size-12 items-center justify-center rounded-md border bg-background disabled:opacity-40"
          >
            <Minus aria-hidden className="size-5" />
          </button>
          <button
            type="button"
            aria-label={t("shift.count_of", { value: name, count })}
            aria-pressed={active === value}
            disabled={disabled}
            onClick={() => setActive(value)}
            data-testid={`count-${String(value)}`}
            className="min-h-12 w-16 rounded-md border bg-background text-2xl font-semibold tabular-nums"
          >
            {count}
          </button>
          <button
            type="button"
            aria-label={t("shift.count_plus", { value: name })}
            disabled={disabled}
            onClick={() => {
              setActive(value);
              set(value, count + 1);
            }}
            className="flex size-12 items-center justify-center rounded-md border bg-background disabled:opacity-40"
          >
            <Plus aria-hidden className="size-5" />
          </button>
        </span>
        <MoneyText
          amount={rowTotal(value, count)}
          className="text-right text-muted-foreground"
          size="md"
        />
      </div>
    );
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_17rem] gap-3">
      <fieldset className="flex min-h-0 flex-col gap-2" disabled={disabled}>
        <legend className="mb-1 text-sm font-medium text-muted-foreground">
          {t("shift.notes")}
        </legend>
        <div className="flex flex-col gap-2">{notes.map(row)}</div>
      </fieldset>
      <fieldset className="flex min-h-0 flex-col gap-2" disabled={disabled}>
        <legend className="mb-1 text-sm font-medium text-muted-foreground">
          {t("shift.coins")}
        </legend>
        <div className="flex flex-col gap-2">{COINS.map(row)}</div>
      </fieldset>
      <div className="flex min-h-0 flex-col gap-2">
        <NumPad
          label={t("shift.count_pad", { value: yen(active) })}
          value={String(counts[active] ?? 0)}
          onChange={(digits) => set(active, Number.parseInt(digits || "0", 10))}
          maxLength={4}
          disabled={disabled}
        />
        <p className="rounded-md border bg-muted px-3 py-2" aria-live="polite">
          <span className="block text-sm text-muted-foreground">{label}</span>
          <MoneyText amount={total} size="xl" data-testid="count-total" />
        </p>
        {children}
      </div>
    </div>
  );
}
